import { useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import toast from 'react-hot-toast';
import { Plus, X } from 'lucide-react';
import { FROZEN, isWeekend } from './tableChrome';
import { dayLabel } from './mediaPlanningView';
import { useCreatePromoMutation, useDeletePromoMutation } from '../../queries/mediaPlanning';

const LABEL = `${FROZEN} z-20 whitespace-nowrap border-b border-gray-100 px-4 py-1.5 text-left`;

/**
 * Informational "Promos" row: colored bands over the days a promo runs, with a
 * "+" to add one. A promo is just a named date range ("a promo is coming") — it
 * does NOT change budgets; the planner reads it and budgets by hand.
 */
export default function PromoBand({ brandId, month, promos = [], days = [], today }) {
  const emptyForm = { name: '', from_date: `${month}-01`, to_date: `${month}-01` };
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const createPromo = useCreatePromoMutation(brandId, month);
  const deletePromo = useDeletePromoMutation(brandId, month);

  const promoOn = (iso) => promos.find((p) => p.from_date <= iso && iso <= p.to_date);

  const submit = async () => {
    const name = form.name.trim();
    if (!name || !form.from_date || !form.to_date) {
      toast.error('Completá nombre y fechas');
      return;
    }
    if (form.to_date < form.from_date) {
      toast.error('La fecha de fin debe ser posterior o igual al inicio');
      return;
    }
    try {
      await createPromo.mutateAsync({ name, from_date: form.from_date, to_date: form.to_date });
      toast.success('Promo agregada');
      setAdding(false);
      setForm(emptyForm);
    } catch (e) {
      toast.error(e?.response?.data?.error || 'No se pudo agregar la promo');
    }
  };

  const remove = async (promo) => {
    try {
      await deletePromo.mutateAsync(promo.id);
    } catch (_e) {
      toast.error('No se pudo quitar la promo');
    }
  };

  return (
    <>
      <tr className="group">
        <th className={`${LABEL} font-medium text-gray-600`}>
          <span className="inline-flex items-center gap-1.5">
            Promos
            <button
              type="button"
              onClick={() => setAdding(true)}
              className="text-gray-400 hover:text-primary"
              aria-label="Agregar promo"
              title="Agregar promo"
            >
              <Plus size={14} />
            </button>
          </span>
        </th>
        {days.map((d) => {
          const p = promoOn(d.date);
          const start = p && p.from_date === d.date;
          const end = p && p.to_date === d.date;
          const isGlobal = p && p.global;  // shared commercial calendar entry
          const weekend = isWeekend(dayLabel(d.date).weekday) ? 'bg-gray-50/60' : '';
          return (
            <td
              key={d.date}
              className={`relative border-b border-gray-100 px-0 py-1.5 ${d.date === today ? 'bg-indigo-50' : weekend}`}
            >
              {p && (
                <div
                  className={`h-4 ${isGlobal ? 'bg-sky-200' : 'bg-fuchsia-200'} ${start ? 'ml-1 rounded-l-full' : ''} ${end ? 'mr-1 rounded-r-full' : ''}`}
                  title={`${p.name}${isGlobal ? ' (calendario)' : ''} · ${p.from_date} → ${p.to_date}`}
                />
              )}
              {start && (
                <span
                  className={`pointer-events-none absolute left-2 top-1/2 z-10 -translate-y-1/2 whitespace-nowrap text-[11px] font-medium ${isGlobal ? 'text-sky-900' : 'text-fuchsia-900'}`}
                >
                  {p.name}
                </span>
              )}
              {start && !isGlobal && (
                <button
                  type="button"
                  onClick={() => remove(p)}
                  className="absolute -right-0.5 -top-0.5 z-20 hidden rounded-full bg-white text-fuchsia-500 hover:text-fuchsia-700 group-hover:block"
                  aria-label={`Quitar promo ${p.name}`}
                >
                  <X size={11} />
                </button>
              )}
            </td>
          );
        })}
      </tr>

      {adding &&
        createPortal(
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
            onClick={() => setAdding(false)}
          >
            <div className="w-full max-w-sm rounded-card bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-base font-semibold text-gray-900">Agregar promo</h3>
              <p className="mt-1 text-sm text-gray-500">
                Marca las fechas en que se viene una promo en el sitio. No cambia presupuestos.
              </p>
              <div className="mt-4 space-y-3">
                <label className="block text-sm">
                  <span className="text-gray-600">Nombre</span>
                  <input
                    type="text"
                    autoFocus
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Ej: Hot Sale"
                    className="mt-1 w-full rounded-lg border border-gray-300 px-2.5 py-1.5 text-sm"
                  />
                </label>
                <div className="flex gap-3">
                  <label className="block flex-1 text-sm">
                    <span className="text-gray-600">Desde</span>
                    <input
                      type="date"
                      value={form.from_date}
                      onChange={(e) => setForm({ ...form, from_date: e.target.value })}
                      className="mt-1 w-full rounded-lg border border-gray-300 px-2.5 py-1.5 text-sm"
                    />
                  </label>
                  <label className="block flex-1 text-sm">
                    <span className="text-gray-600">Hasta</span>
                    <input
                      type="date"
                      value={form.to_date}
                      onChange={(e) => setForm({ ...form, to_date: e.target.value })}
                      className="mt-1 w-full rounded-lg border border-gray-300 px-2.5 py-1.5 text-sm"
                    />
                  </label>
                </div>
              </div>
              <div className="mt-5 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setAdding(false)}
                  className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={submit}
                  disabled={createPromo.isPending}
                  className="rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-50"
                >
                  {createPromo.isPending ? 'Guardando…' : 'Guardar'}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}

PromoBand.propTypes = {
  brandId: PropTypes.number.isRequired,
  month: PropTypes.string.isRequired,
  promos: PropTypes.array,
  days: PropTypes.array,
  today: PropTypes.string,
};
